import { FormEvent, useEffect, useRef, useState } from 'react'
import { consultarFiqueEmDia, obterStatusFiqueEmDia, type FiqueConsulta, type FiqueStatus } from '../../lib/fiqueEmDia'
import { documentoValido, mascaraDocumento } from '../../lib/fiqueEmDiaParser'
import { prepararParcelamentoCampanha, type ParcelamentoCampanha } from '../../lib/parcelamentoCampanha'

const ETAPAS = [
  { titulo: 'Quitação à vista', subtitulo: 'Fatura a fatura', orientacao: 'Início da negociação: apresente primeiro a quitação à vista com o desconto base.' },
  { titulo: 'Parcelamento', subtitulo: 'Fatura a fatura', orientacao: 'Alternativa quando o cliente não consegue realizar o pagamento à vista.' },
  { titulo: 'Quitação especial', subtitulo: 'Atraso mais antigo', orientacao: 'Condição superior após as tentativas anteriores, respeitando a estratégia da campanha.' },
  { titulo: 'Parcelamento especial', subtitulo: 'Último recurso', orientacao: 'Última etapa da negociação: verifique a alçada antes de oferecer esta condição.' },
] as const

function moeda(value: number) {
  return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

function dataBR(value?: string | null) {
  const date = value?.slice(0, 10)
  if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return 'Não informado'
  return date.slice(8, 10) + '/' + date.slice(5, 7) + '/' + date.slice(0, 4)
}

function formatarEntrada(value: string) {
  const digits = value.replace(/\D/g, '').slice(0, 14)
  if (digits.length >= 12) {
    return digits.slice(0, 2) + '.' + digits.slice(2, 5) + '.' + digits.slice(5, 8) +
      '/' + digits.slice(8, 12) + (digits.length > 12 ? '-' + digits.slice(12) : '')
  }
  if (digits.length <= 3) return digits
  if (digits.length <= 6) return digits.slice(0, 3) + '.' + digits.slice(3)
  if (digits.length <= 9) return digits.slice(0, 3) + '.' + digits.slice(3, 6) + '.' + digits.slice(6)
  return mascaraDocumento(digits)
}

export function FiqueEmDiaConsulta({ onSimularParcelamento }: { onSimularParcelamento: (dados: ParcelamentoCampanha) => void }) {
  const [cpf, setCpf] = useState('')
  const [status, setStatus] = useState<FiqueStatus | null>(null)
  const [statusLoading, setStatusLoading] = useState(true)
  const [consultando, setConsultando] = useState(false)
  const [resposta, setResposta] = useState<FiqueConsulta | null>(null)
  const [erro, setErro] = useState('')
  const [registro, setRegistro] = useState(0)
  const [etapa, setEtapa] = useState(0)
  const consultaAtual = useRef(0)

  useEffect(() => {
    let active = true
    obterStatusFiqueEmDia()
      .then((data) => { if (active) setStatus(data) })
      .catch(() => { if (active) setErro('Não foi possível verificar a base. Tente novamente mais tarde.') })
      .finally(() => { if (active) setStatusLoading(false) })
    return () => { active = false }
  }, [])

  function limpar() {
    consultaAtual.current++
    setCpf('')
    setResposta(null)
    setErro('')
    setRegistro(0)
    setEtapa(0)
    setConsultando(false)
  }

  async function buscar(event: FormEvent) {
    event.preventDefault()
    const digits = cpf.replace(/\D/g, '')
    if (!documentoValido(digits)) {
      setErro('CPF ou CNPJ inválido. Confira os dígitos informados.')
      setResposta(null)
      return
    }
    const sequence = ++consultaAtual.current
    setErro('')
    setResposta(null)
    setConsultando(true)
    setRegistro(0)
    setEtapa(0)
    try {
      const result = await consultarFiqueEmDia(digits)
      if (sequence !== consultaAtual.current) return
      setResposta(result)
      if (result.referencia) setStatus({
        possui_base: result.status !== 'sem_base',
        referencia: result.referencia,
        atualizado_em: result.atualizado_em ?? null,
      })
    } catch {
      if (sequence === consultaAtual.current) setErro('Não foi possível concluir a consulta. Tente novamente.')
    } finally {
      if (sequence === consultaAtual.current) setConsultando(false)
    }
  }

  const clientes = resposta?.status === 'elegivel' ? resposta.registros ?? [] : []
  const selecionado = clientes[registro]
  const oferta = selecionado?.etapas[etapa]
  const dadosParcelamento = selecionado && oferta && (etapa === 1 || etapa === 3)
    ? prepararParcelamentoCampanha(etapa === 1 ? 2 : 4, Number(oferta.valor_final), selecionado.max_parcelas)
    : null
  const referencia = resposta?.referencia ?? status?.referencia

  return (
    <section className="operational-tool fique-em-dia-tool">
      <div className="tool-heading">
        <div>
          <p className="eyebrow">CONSULTA OPERACIONAL</p>
          <h2>Fique em Dia 2026: Consulta por CPF/CNPJ</h2>
          <p className="muted">Verifique a presença do cliente na base vigente e consulte as etapas da campanha.</p>
        </div>
        <span className="status-chip">Consulta protegida</span>
      </div>

      <section className="tool-card fique-query">
        <form onSubmit={buscar} autoComplete="off">
          <label htmlFor="fique-cpf">CPF ou CNPJ do cliente</label>
          <div className="fique-search-row">
            <input id="fique-cpf" type="text" inputMode="numeric" autoComplete="off" maxLength={18}
              placeholder="CPF ou CNPJ" value={cpf} autoFocus
              onChange={(event) => {
                consultaAtual.current++
                setCpf(formatarEntrada(event.target.value))
                setResposta(null)
                setErro('')
                setConsultando(false)
              }}/>
            <button type="submit" className="primary-button compact" disabled={consultando || !documentoValido(cpf)}>
              {consultando ? 'Consultando...' : 'Consultar'}
            </button>
            {(cpf || resposta) && <button type="button" className="secondary-button compact" onClick={limpar}>Limpar</button>}
          </div>
        </form>
        <p className="fique-muted">Apenas consulta individual. A relação completa de CPFs e CNPJs não está disponível nesta área.</p>
        {statusLoading && <div className="tool-status info">Verificando disponibilidade da base...</div>}
        {!statusLoading && status && !status.possui_base && <div className="tool-status warning">Ainda não existe uma base publicada para esta campanha.</div>}
        {status?.possui_base && <p className="fique-reference">Base vigente: referência <strong>{dataBR(status.referencia)}</strong></p>}
        {[11, 14].includes(cpf.replace(/\D/g, '').length) && !documentoValido(cpf) && <p className="fique-invalid">O CPF/CNPJ informado não passou na validação dos dígitos verificadores.</p>}
        {erro && <div className="tool-status error" role="alert">{erro}</div>}
      </section>

      {resposta && (
        <section className="tool-card fique-answer" aria-live="polite">
          {resposta.status === 'nao_encontrado' && (
            <div className="fique-result-state warning">
              <strong>Documento não consta na relação da campanha</strong>
              <p>Não foi localizado na base vigente de elegíveis. Para outras alternativas, utilize o simulador de negociação já disponível na Suite.</p>
            </div>
          )}
          {resposta.status === 'invalido' && <div className="fique-result-state warning">CPF/CNPJ inválido. Confira os números informados.</div>}
          {resposta.status === 'sem_base' && <div className="fique-result-state warning">Nenhuma base vigente disponível. Solicite a publicação ao ADMIN.</div>}
          {resposta.status === 'inconsistente' && <div className="fique-result-state warning">Há inconsistências nos registros. Solicite conferência ao ADMIN.</div>}
          {resposta.status === 'limite' && <div className="fique-result-state warning">Limite temporário de consultas atingido. Aguarde um minuto antes de tentar novamente.</div>}
          {resposta.status === 'elegivel' && selecionado && (
            <>
              <div className="fique-found-heading">
                <div><span className="fique-eligible">✓ Elegível à campanha</span><h3>{selecionado.nome}</h3></div>
                {selecionado.cliente_social && <span className="fique-social">Cliente Social</span>}
              </div>
              {clientes.length > 1 && (
                <label className="fique-multiple">Registro vinculado
                  <select value={registro} onChange={(event) => { setRegistro(Number(event.target.value)); setEtapa(0) }}>
                    {clientes.map((client, index) => (
                      <option key={index} value={index}>Matrícula(s): {client.matriculas}</option>
                    ))}
                  </select>
                  <small>O documento possui mais de um registro. Consulte cada matrícula separadamente.</small>
                </label>
              )}
              <p className="fique-matricula">Matrícula(s): <strong>{selecionado.matriculas}</strong></p>
              <div className="fique-metrics">
                <div><span>Saldo da base</span><strong>{moeda(selecionado.saldo)}</strong></div>
                <div><span>Dias em atraso (hoje)</span><strong>{selecionado.dias}</strong></div>
                <div><span>Faturas pendentes</span><strong>{selecionado.faturas}</strong></div>
                <div><span>Vencimento mais antigo</span><strong>{dataBR(selecionado.vencimento)}</strong></div>
              </div>

              <h3 className="fique-section-title">Etapas de negociação <small>Sequência recomendada</small></h3>
              <div className="fique-stages" role="group" aria-label="Etapas da campanha">
                {ETAPAS.map((item, index) => (
                  <button type="button" key={item.titulo} className={'fique-stage ' + (etapa === index ? 'is-selected' : '')}
                    onClick={() => setEtapa(index)} aria-pressed={etapa === index}>
                    <span className="fique-stage-num">{index + 1}</span>
                    <strong>{item.titulo}</strong>
                    <small>{item.subtitulo}</small>
                    <b>{moeda(selecionado.etapas[index]?.valor_final ?? 0)}</b>
                  </button>
                ))}
              </div>

              {oferta && (
                <div className="fique-step-detail">
                  <span className="fique-step-tag">Etapa {etapa + 1} de 4{etapa === 3 ? ': Último recurso' : ''}</span>
                  <h4>{ETAPAS[etapa].titulo}: {ETAPAS[etapa].subtitulo}</h4>
                  <p>{ETAPAS[etapa].orientacao}</p>
                  <div className="fique-step-values">
                    <div><span>Saldo original</span><strong>{moeda(selecionado.saldo)}</strong></div>
                    <div><span>Desconto</span><strong>{moeda(oferta.desconto)}</strong></div>
                    <div><span>Valor final</span><strong>{moeda(oferta.valor_final)}</strong></div>
                  </div>
                  <div className={'fique-step-actions ' + (etapa === 0 ? 'only-next' : '')}>
                    {etapa > 0 && <button type="button" className="secondary-button compact" onClick={() => setEtapa(etapa - 1)}>← Etapa anterior</button>}
                    <div className="fique-step-action-main">
                      {dadosParcelamento && (
                        <button type="button" className="primary-button compact"
                          onClick={() => onSimularParcelamento(dadosParcelamento)}>
                          Simular parcelamento com este valor →
                        </button>
                      )}
                      {etapa < 3 && <button type="button" className={dadosParcelamento ? 'secondary-button compact' : 'primary-button compact'} onClick={() => setEtapa(etapa + 1)}>Próxima etapa →</button>}
                    </div>
                  </div>
                </div>
              )}
              <p className="fique-reference">Dias em atraso calculados automaticamente a cada consulta pelo vencimento mais antigo. Saldos e condições comerciais conforme a base de <strong>{dataBR(referencia)}</strong>; valores não são atualizados em tempo real.</p>
            </>
          )}
        </section>
      )}
    </section>
  )
}
